import type { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { REGEX } from '@utils/validationUtils';

import { PhoneType } from 'src/enums';

export const initialValues = {
  type: '',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  companyName: '',
  email: '',
  phoneType: PhoneType.MOBILE,
  phoneNumber: '',
  isTaxable: true,
  occupationClass: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  addressType: '',
  country: '',
  zone: '',
  city: '',
  street: '',
  building: '',
  addressDetails: '',
  addressDetailsArabic: '',
  passportNumber: ''
};

export const getInputs = (
  values: Record<string, string | boolean> = initialValues,
  lovs: Record<string, Record<string, string>> = {},
  section: string
): IDynamicFormWidgetSection[] => {
  const isPerson = values.type === 'INDIVIDUAL';
  const isCompany = values.type === 'COMPANY';

  return [
    {
      sectionTitle: 'Person Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          type: FormFieldType.select,
          placeholder: 'Type',
          value: values.type,
          required: true,
          selectOptions: lovs.PersonTypes
        },
        title: {
          name: 'title',
          title: 'Title',
          placeholder: 'Title',
          type: FormFieldType.select,
          value: values.title,
          required: isPerson,
          hidden: !isPerson,
          selectOptions: lovs.titles
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          placeholder: 'First Name',
          type: FormFieldType.text,
          value: values.firstName,
          required: isPerson,
          hidden: !isPerson
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          placeholder: 'Middle Name',
          type: FormFieldType.text,
          value: values.middleName,
          required: isPerson,
          hidden: !isPerson
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          placeholder: 'Last Name',
          type: FormFieldType.text,
          value: values.lastName,
          required: isPerson,
          hidden: !isPerson
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: values.dateOfBirth,
          hidden: !isPerson,
          maxDate: new Date(),
          required: true
        },
        nationality: {
          name: 'nationality',
          title: 'Nationality',
          placeholder: 'Nationality',
          type: FormFieldType.select,
          value: values.nationality,
          hidden: !isPerson,
          selectOptions: lovs.nationalities,
          required: false,
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          placeholder: 'Company Name',
          type: FormFieldType.text,
          value: values.companyName,
          hidden: !isCompany,
          required: isCompany
        },
        email: {
          name: 'email',
          title: 'Email',
          placeholder: 'Email',
          type: FormFieldType.text,
          value: values.email,
          customValidator: (values) => {
            if (values.email && !REGEX.email.test(values.email)) {
              return 'Invalid Email Address';
            }

            return '';
          },
          required: false
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          placeholder: 'Phone Type',
          type: FormFieldType.select,
          value: values.phoneType,
          required: true,
          selectOptions: lovs.phoneType
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          placeholder: 'Phone Number',
          type: FormFieldType.phoneNumber,
          value: values.phoneNumber,
          required: false
        }
      }
    },
    {
      sectionTitle: 'Primary Address Details',
      inputs: {
        addressType: {
          name: 'addressType',
          title: 'Address Type',
          placeholder: 'Select Address Type',
          type: FormFieldType.select,
          value: values.addressType,
          required: true,
          selectOptions: lovs.addressType
        },
        country: {
          name: 'country',
          title: 'Country',
          placeholder: 'Country',
          type: FormFieldType.select,
          value: values.country,
          required: true,
          selectOptions: lovs.countries
        },
        zone: {
          name: 'zone',
          title: 'Zone',
          placeholder: 'Zone',
          type: FormFieldType.select,
          value: values.zone,
          hidden: values.country !== 'LB',
          selectOptions: values.country === 'LB' ? lovs.zones : {}
        },
        city: {
          name: 'city',
          title: 'City',
          placeholder: 'City',
          type: FormFieldType.text,
          value: values.city,
          required: true
        },
        street: {
          name: 'street',
          title: 'Street',
          placeholder: 'Street',
          type: FormFieldType.text,
          value: values.street
        },
        building: {
          name: 'building',
          title: 'Building',
          placeholder: 'Building',
          type: FormFieldType.text,
          value: values.building
        },
        addressDetails: {
          name: 'addressDetails',
          title: 'Address Details',
          placeholder: 'Address Details',
          type: FormFieldType.text,
          value: values.addressDetails
        },
        addressDetailsArabic: {
          name: 'addressDetailsArabic',
          title: 'Address Details Arabic',
          placeholder: 'Address Details in Arabic',
          type: FormFieldType.text,
          value: values.addressDetailsArabic
        }
      }
    }
  ];
};
